import React from 'react';
import { Handle, Position } from 'reactflow';
import { Edit2, Plus, Trash2 } from 'lucide-react';

export const RootNode = ({ data, selected }) => (
  <div
    className={`px-6 py-4 bg-[#1A1B1E] rounded-lg shadow-lg border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'}`}
  >
    <div className="text-white font-bold text-lg">{data.label}</div>
    <div className="text-gray-400 text-xs mt-1">Asset Root</div>
    <Handle type="source" position={Position.Bottom} />
  </div>
);

export const CategoryNode = ({ data, selected }) => (
  <div
    className={`px-5 py-3 bg-[#2B2C2F] rounded-lg shadow-md border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'}`}
  >
    <Handle type="target" position={Position.Top} />
    <div className="text-white font-semibold">{data.label}</div>
    <Handle type="source" position={Position.Bottom} />
  </div>
);

export const SubCategoryNode = ({ data, selected }) => {
  const handleDelete = (e) => {
    e.stopPropagation();
    if (
      data.onDelete &&
      globalThis.confirm('Are you sure you want to delete this event?')
    ) {
      data.onDelete();
    }
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (data.onEdit) {
      data.onEdit();
    }
  };

  const isEvent = !!data.originalData?.eventData;

  return (
    <div
      className={`px-4 py-2 bg-[#2B2C2F] rounded-lg shadow-sm border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'} ${isEvent ? 'min-w-[200px]' : ''}`}
    >
      <Handle type="target" position={Position.Top} />
      <div className="text-white font-medium text-sm">{data.label}</div>
      {isEvent && (
        <div className="flex gap-2 mt-2">
          {data.onEdit && (
            <button
              onClick={handleEdit}
              className="flex-1 px-2 py-1 bg-[#37383F] hover:bg-[#4a4b50] rounded text-xs text-white flex items-center justify-center gap-1 transition"
            >
              <Edit2 className="w-3 h-3" />
              Edit
            </button>
          )}
          {data.onDelete && (
            <button
              onClick={handleDelete}
              className="px-2 py-1 bg-[#EE2B2A] hover:bg-[#d02524] rounded text-xs text-white flex items-center justify-center gap-1 transition"
            >
              <Trash2 className="w-3 h-3" />
              Delete
            </button>
          )}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

export const PropertyNode = ({ data, selected }) => {
  const handleEdit = () => {
    if (data.onEdit) {
      data.onEdit(data);
    }
  };

  const isTrigger = data.label === 'VR Trigger';
  const hasAction = data.hasAction !== false;

  return (
    <div
      className={`px-4 py-2 bg-[#2B2C2F] rounded-lg shadow-sm border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'} min-w-[180px] ${isTrigger && !hasAction ? 'cursor-pointer' : ''}`}
    >
      <Handle type="target" position={Position.Top} />
      <div className="text-white text-sm">
        <div className="font-medium">{data.label}</div>
        <div className="text-xs mt-1 text-gray-400">
          {data.value !== undefined ? String(data.value) : 'N/A'}
        </div>
        {isTrigger && !hasAction && (
          <div className="text-xs mt-1 italic text-gray-500">
            Click to add action
          </div>
        )}
      </div>
      {data.editable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleEdit();
          }}
          className="mt-2 px-2 py-1 bg-[#37383F] hover:bg-[#4a4b50] rounded text-xs text-white flex items-center gap-1 transition"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      )}
      {isTrigger && hasAction && (
        <Handle type="source" position={Position.Bottom} />
      )}
    </div>
  );
};

export const TriggerNode = ({ data, selected }) => {
  const handleEdit = () => {
    if (data.onEdit) {
      data.onEdit(data);
    }
  };

  return (
    <div
      className={`px-5 py-3 bg-[#2B2C2F] rounded-lg shadow-md border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'} min-w-[200px]`}
    >
      <Handle type="target" position={Position.Top} id="trigger-input" />
      <div className="text-white">
        <div className="font-semibold text-sm mb-1">Trigger</div>
        <div className="text-xs text-gray-400">{data.value || 'None'}</div>
      </div>
      <div className="flex gap-2 mt-2">
        <Handle
          type="source"
          position={Position.Right}
          id="action-output"
          style={{
            top: '30%',
            background: '#EE2B2A',
            width: '12px',
            height: '12px',
          }}
        />
        <div className="flex-1 text-xs text-gray-400">Actions</div>
      </div>
      <div className="flex gap-2 mt-1">
        <Handle
          type="source"
          position={Position.Right}
          id="trigger-output"
          style={{
            top: '70%',
            background: '#EE2B2A',
            width: '12px',
            height: '12px',
          }}
        />
        <div className="flex-1 text-xs text-gray-400">More Triggers</div>
      </div>
      {data.editable && (
        <button
          onClick={handleEdit}
          className="mt-2 px-2 py-1 bg-[#37383F] hover:bg-[#4a4b50] rounded text-xs text-white flex items-center gap-1 w-full transition"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      )}
    </div>
  );
};

export const ActionNode = ({ data, selected }) => {
  const handleEdit = () => {
    if (data.onEdit) {
      data.onEdit(data);
    }
  };

  return (
    <div
      className={`px-5 py-3 bg-[#2B2C2F] rounded-lg shadow-md border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'} min-w-[200px]`}
    >
      <Handle type="target" position={Position.Left} />
      <div className="text-white">
        <div className="font-semibold text-sm mb-1">Action</div>
        <div className="text-xs text-gray-400">{data.value || 'None'}</div>
      </div>
      <Handle type="source" position={Position.Right} />
      {data.editable && (
        <button
          onClick={handleEdit}
          className="mt-2 px-2 py-1 bg-[#37383F] hover:bg-[#4a4b50] rounded text-xs text-white flex items-center gap-1 w-full transition"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      )}
    </div>
  );
};

export const EventContainerNode = ({ data, selected }) => {
  const handleAdd = () => {
    if (data.onAdd) {
      data.onAdd();
    }
  };

  return (
    <div
      className={`px-5 py-3 bg-[#2B2C2F] rounded-lg shadow-md border-2 ${selected ? 'border-[#EE2B2A]' : 'border-[#37383F]'}`}
    >
      <Handle type="target" position={Position.Top} />
      <div className="text-white font-semibold">{data.label}</div>
      <button
        onClick={handleAdd}
        className="mt-2 px-3 py-1 bg-[#37383F] hover:bg-[#4a4b50] rounded text-xs text-white flex items-center gap-1 transition"
      >
        <Plus className="w-3 h-3" />
        Add Event
      </button>
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};
